import React from 'react';
import { Link } from 'react-router';
import { Code, BookOpen, Palette, MapPin, Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.jsx';
import Header from '../components/common/Header.jsx';
import Footer from '../components/common/Footer.jsx';

function AboutUsContent() {
  const { colors, spacing, fonts, borderRadius, shadows } = useTheme();

  const pageStyles = {
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.families.primary,
    lineHeight: fonts.lineHeights.normal,
    transition: 'all 0.3s ease'
  };

  const mainStyles = {
    maxWidth: '900px',
    margin: `${spacing.scale.xl} auto`,
    padding: `0 ${spacing.scale.lg}`
  };

  const contentContainerStyles = {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.md,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`
  };

  const contentStyles = {
    padding: `${spacing.scale.xl} ${spacing.scale.lg}`
  };

  const sectionStyles = {
    marginBottom: spacing.scale.xl
  };

  const headerSectionStyles = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: colors.textInverse,
    padding: `${spacing.scale.lg} ${spacing.scale.md}`,
    textAlign: 'center'
  };

  const titleStyles = {
    fontSize: fonts.sizes['2xl'].desktop,
    marginBottom: 0,
    fontWeight: fonts.weights.bold,
    fontFamily: fonts.families.secondary,
    color: '#ffffff',
    textShadow: `1px 1px 2px rgba(0, 0, 0, 0.5)`
  };

  return (
    <div style={pageStyles}>
      <Header />

      <main style={mainStyles}>
        <div style={contentContainerStyles}>
          <section style={headerSectionStyles}>
            <h1 style={titleStyles}>About Victoria Londe</h1>
          </section>

          <div style={contentStyles}>
            {/* Introduction */}
            <section style={{ marginBottom: spacing.scale.lg }}>
              <p style={{
                fontSize: fonts.sizes.base.desktop,
                color: colors.text,
                lineHeight: fonts.lineHeights.relaxed,
                marginBottom: spacing.scale.md
              }}>
                Welcome to Victoria Londe, where Software meets Stories meets Stuff. We are a Bay Area sole proprietorship working across software, writing, and design. Our software lives at LondaLab, our lab for Human Centered Intelligent Apps, alongside writing and projects that don't fit neatly into a single category. We are actively building, iterating, and creating new things alongside the people who use them.
              </p>
            </section>

            {/* Vision Box */}
            <div id="vision" style={{
              backgroundColor: `${colors.info}10`,
              border: `1px solid ${colors.info}40`,
              borderRadius: borderRadius.md,
              padding: spacing.scale.lg,
              marginBottom: spacing.scale.xl
            }}>
              <h3 style={{
                color: colors.info,
                marginTop: 0,
                marginBottom: spacing.scale.sm,
                fontSize: fonts.sizes.base.desktop,
                fontWeight: fonts.weights.semibold,
                fontFamily: fonts.families.secondary
              }}>
                Our Vision
              </h3>
              <p style={{
                fontSize: fonts.sizes.sm.desktop,
                color: colors.text,
                lineHeight: fonts.lineHeights.relaxed,
                marginBottom: 0
              }}>
                At Victoria Londe, we believe technology should empower and inspire. Whether we're building apps that help people in their everyday lives, crafting educational content that sparks curiosity, or developing entertainment that brings joy, we're committed to excellence in everything we create.
              </p>
            </div>

            {/* What We Do */}
            <section style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                What We Do
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.scale.md }}>
                <div id="software">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <Code size={20} color={colors.secondary} aria-hidden="true" /> Our Apps: LondaLab
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    All our apps are published through{' '}
                    <a
                      href="https://londalab.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: colors.secondary,
                        textDecoration: 'none',
                        fontWeight: fonts.weights.medium
                      }}
                    >
                      LondaLab
                    </a>
                    , our lab for Human Centered Intelligent Apps. From everyday life to learning and literacy to small business tools, LondaLab is where we design, build, and publish apps that help people.
                  </p>
                </div>

                <div id="content">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <BookOpen size={20} color={colors.secondary} aria-hidden="true" /> Digital Content & Stories
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    Our creative team develops engaging educational content, including early reader materials, ESL resources, and original video productions. We're storytellers at heart, making learning accessible and enjoyable.
                  </p>
                </div>

                <div id="products">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <Palette size={20} color={colors.secondary} aria-hidden="true" /> Creative Products
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    From branded merchandise to workspace essentials, we design products that inspire creativity and productivity. Every item reflects our commitment to quality and thoughtful design.
                  </p>
                </div>
              </div>
            </section>

            {/* Our Values */}
            <section id="values" style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                Our Values
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.scale.sm }}>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Innovation:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We embrace new technologies and creative approaches to solve complex problems.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Quality:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    Excellence isn't just a goal, it's our standard. We take pride in delivering products that exceed expectations.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Community:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We build tools and content that bring people together and foster collaboration.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Integrity:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We operate with transparency, ethical practices, and respect for our users and partners.
                  </span>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section id="contact" style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                Contact Information
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.scale.md
              }}>
                <div style={{
                  backgroundColor: colors.backgroundSecondary,
                  borderRadius: borderRadius.md,
                  padding: spacing.scale.md,
                  border: `1px solid ${colors.border}`,
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing.scale.sm,
                    marginBottom: spacing.scale.xs
                  }}>
                    <MapPin size={18} color={colors.secondary} aria-hidden="true" />
                    <h3 style={{
                      fontSize: fonts.sizes.base.desktop,
                      fontWeight: fonts.weights.semibold,
                      color: colors.text,
                      margin: 0,
                      fontFamily: fonts.families.secondary
                    }}>
                      Business Address
                    </h3>
                  </div>
                  <div style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.relaxed
                  }}>
                    <strong>Victoria Londe</strong><br />
                    2883 Hopyard Rd<br />
                    Pleasanton, CA 94588
                  </div>
                </div>

                <div style={{
                  backgroundColor: colors.backgroundSecondary,
                  borderRadius: borderRadius.md,
                  padding: spacing.scale.md,
                  border: `1px solid ${colors.border}`,
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing.scale.sm,
                    marginBottom: spacing.scale.xs
                  }}>
                    <Mail size={18} color={colors.secondary} aria-hidden="true" />
                    <h3 style={{
                      fontSize: fonts.sizes.base.desktop,
                      fontWeight: fonts.weights.semibold,
                      color: colors.text,
                      margin: 0,
                      fontFamily: fonts.families.secondary
                    }}>
                      Email
                    </h3>
                  </div>
                  <div style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.relaxed
                  }}>
                    <a
                      href="mailto:hello@victorialonde.com"
                      style={{
                        color: colors.secondary,
                        textDecoration: 'none',
                        fontWeight: fonts.weights.medium
                      }}
                      onMouseEnter={(e) => e.target.style.color = colors.secondaryHover}
                      onMouseLeave={(e) => e.target.style.color = colors.secondary}
                    >
                      hello@victorialonde.com
                    </a>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default function AboutPage() {
  return <AboutUsContent />;
}
